import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Building2, User, Mail, KeyRound, Phone, MapPin, Loader2,
  ArrowRight, ArrowLeft, Check, CreditCard, Thermometer,
  ShoppingCart, Users, ChevronRight, Sparkles, Tag, Star, Bot, Store,
  FileText, ScrollText
} from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURE_LABELS = {
  haccp_temperature: "Controllo temperature", haccp_pulizie: "Piano pulizie",
  haccp_lotti: "Tracciabilità lotti", haccp_alert: "Alert temperature",
  task_gestione: "Bacheca compiti", calendario_attivita: "Calendario",
  dashboard: "Dashboard finanziaria", acquisti_fornitori: "Fornitori",
  acquisti_ingredienti: "Materie prime & Categorie", acquisti_ricette: "Ricette & Sotto-ricette",
  acquisti_fatture: "Import fatture da provider", acquisti_prima_nota: "Cassa & Contabilità",
  acquisti_alert_scorte: "Alert scorte", acquisti_attrezzature: "Attrezzature & Packaging",
  personale_utenti: "Gestione operatori", personale_turni: "Turni & Calendario",
  personale_presenze: "Presenze", bot_telegram: "Bot Telegram",
  locale_aggiuntivo: "Locale Aggiuntivo",
};

const TIPO_ICONS = { base: Thermometer, addon: ShoppingCart, servizio: Bot };
const TIPO_COLORS = {
  base:     { bg: "bg-indigo-50", border: "border-indigo-300", accent: "#6C63FF", check: "text-indigo-500" },
  addon:    { bg: "bg-amber-50",  border: "border-amber-300",  accent: "#F59E0B", check: "text-amber-500" },
  servizio: { bg: "bg-teal-50",   border: "border-teal-300",   accent: "#14B8A6", check: "text-teal-500" },
};

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const ppToken = searchParams.get("token");
  const ppProvider = searchParams.get("provider");
  const ppStatus = searchParams.get("status");

  const [step, setStep] = useState((sessionId || (ppToken && ppProvider === "paypal")) ? 4 : 0);
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [comboRules, setComboRules] = useState([]);
  const [addonsConfig, setAddonsConfig] = useState({});
  const [ivaPct, setIvaPct] = useState(22);
  const [acquiKeyword, setAcquiKeyword] = useState("acquisti");
  const [trialInfo, setTrialInfo] = useState({ attivo: false, giorni: 0 });
  // hasTrial: il trial è attivo SOLO se attivo=true E giorni>0
  const hasTrial = trialInfo.attivo && (trialInfo.giorni || 0) > 0;
  const [selectedPkgs, setSelectedPkgs] = useState([]);
  const [addonTelegramQty, setAddonTelegramQty] = useState(0);
  const [addonLocaleExtra, setAddonLocaleExtra] = useState(0);
  const [cart, setCart] = useState(null);

  // Discount code
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [activePromo, setActivePromo] = useState(null);

  const [form, setForm] = useState({
    nome_azienda: "", partita_iva: "", sede_legale: "", codice_fiscale: "",
    codice_univoco: "", pec: "", email_azienda: "",
    nome_admin: "", email_admin: "",
    password: "", conferma_password: "", telefono: "", nome_locale: ""
  });

  const [paymentStatus, setPaymentStatus] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [pollStopped, setPollStopped] = useState(false);

  // Referral
  const [referralCode, setReferralCode] = useState("");
  const [referralInfo, setReferralInfo] = useState(null);

  // Contratto
  const [contratto, setContratto] = useState(null);
  const [contrattoAccettato, setContrattoAccettato] = useState(false);
  const [contrattoScrolled, setContrattoScrolled] = useState(false);

  useEffect(() => {
    axios.get(`${API}/superadmin/public-pricing-v2`).then(res => {
      const pkgs = res.data.pacchetti || [];
      setPackages(pkgs);
      setComboRules(res.data.combo_rules || []);
      setAddonsConfig(res.data.addons || {});
      setIvaPct(res.data.iva_percentuale || 22);
      setAcquiKeyword(res.data.pacchetto_acquisti_keyword || "acquisti");
      setTrialInfo({ attivo: res.data.trial?.attivo || false, giorni: res.data.trial?.giorni || 0 });
    }).catch(() => {});
  }, []);

  // Server-side cart calculation
  useEffect(() => {
    if (selectedPkgs.length === 0 && addonTelegramQty === 0 && addonLocaleExtra === 0) {
      setCart(null);
      return;
    }
    const t = setTimeout(() => {
      axios.post(`${API}/superadmin/calculate-cart`, {
        package_ids: selectedPkgs,
        addon_telegram_qty: addonTelegramQty,
        addon_locale_extra: addonLocaleExtra,
        codice_sconto: activePromo?.codice || "",
      }).then(res => setCart(res.data)).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [selectedPkgs, addonTelegramQty, addonLocaleExtra, activePromo]);

  // Read referral code from localStorage
  useEffect(() => {
    const code = localStorage.getItem("yollo_referral_code");
    if (code) {
      setReferralCode(code);
      axios.get(`${API}/register/validate-referral/${code}`)
        .then(res => setReferralInfo(res.data))
        .catch(() => {
          localStorage.removeItem("yollo_referral_code");
          setReferralCode("");
        });
    }
  }, []);

  // Load public contract template
  useEffect(() => {
    axios.get(`${API}/superadmin/contratti-template/pubblico/cliente`)
      .then(res => {
        if (res.data?.contenuto) setContratto(res.data);
      })
      .catch(() => {});
  }, []);

  const pollStatus = useCallback(async () => {
    if (pollCount >= 24) { setPollStopped(true); return; }
    // PayPal: capture order
    if (ppToken && ppProvider === "paypal" && ppStatus === "success") {
      try {
        const res = await axios.post(`${API}/register/paypal-capture/${ppToken}`);
        setPaymentStatus(res.data);
      } catch { setPaymentStatus({ status: "error", payment_status: "failed" }); }
      return;
    }
    // Stripe: poll status
    if (!sessionId) return;
    try {
      const res = await axios.get(`${API}/register/checkout-status/${sessionId}`);
      setPaymentStatus(res.data);
      if (res.data.payment_status === "paid" || res.data.status === "expired") return;
      setPollCount(c => c + 1);
      setTimeout(() => pollStatus(), 3000);
    } catch {
      setPollCount(c => c + 1);
      setTimeout(() => pollStatus(), 4000);
    }
  }, [sessionId, ppToken, ppProvider, ppStatus, pollCount]);

  useEffect(() => { if (sessionId && step === 4) pollStatus(); }, [sessionId, step, pollStatus]);

  const togglePkg = (id) => {
    setSelectedPkgs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const totalMensile = () => cart?.totale_mensile || 0;
  const subtotalSenzaIva = () => cart?.subtotal || 0;

  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/superadmin/validate-discount`, { codice: promoCode });
      setActivePromo(res.data);
      toast.success(`Codice "${res.data.codice}" applicato!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Codice non valido");
      setActivePromo(null);
    } finally { setPromoLoading(false); }
  };

  const removePromo = () => { setActivePromo(null); setPromoCode(""); };

  const updateField = (key, value) => {
    if (key === "password" && accountStatus.exists) setAccountStatus(prev => ({ ...prev, verified: false }));
    setForm(f => ({ ...f, [key]: value }));
  };

  // Validazione P.IVA italiana (11 cifre + cifra di controllo Luhn-like)
  const validatePIVA = (piva) => {
    const p = piva.trim().toUpperCase().replace(/^IT/, "");
    if (!/^\d{11}$/.test(p)) return false;
    let sum = 0;
    for (let i = 0; i < 10; i++) {
      const d = parseInt(p[i]);
      if (i % 2 === 0) {
        sum += d;
      } else {
        const x = d * 2;
        sum += x >= 10 ? x - 9 : x;
      }
    }
    return (10 - (sum % 10)) % 10 === parseInt(p[10]);
  };

  const [formErrors, setFormErrors] = useState({});
  const [accountStatus, setAccountStatus] = useState({ state: "idle", exists: null });
  const [verifyingAccount, setVerifyingAccount] = useState(false);

  useEffect(() => {
    if (step !== 2) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email_admin) || !/^\d{7,15}$/.test(form.telefono)) {
      setAccountStatus({ state: "idle", exists: null });
      return;
    }
    setAccountStatus({ state: "checking", exists: null });
    const controller = new AbortController();
    const timer = setTimeout(() => {
      axios.post(`${API}/register/lookup-account`, { email: form.email_admin, telefono: form.telefono },
        { signal: controller.signal })
        .then(res => setAccountStatus({ state: "ready", exists: res.data.exists, verified: false }))
        .catch(err => {
          if (err.code !== "ERR_CANCELED") setAccountStatus({ state: "error", exists: null,
            message: err.response?.data?.detail || "Verifica account non disponibile" });
        });
    }, 550);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [step, form.email_admin, form.telefono]);

  const verifyExistingAccount = async () => {
    if (!form.password) { toast.error("Inserisci la password dell'account esistente"); return; }
    setVerifyingAccount(true);
    try {
      const res = await axios.post(`${API}/register/verify-account`, {
        email: form.email_admin, telefono: form.telefono, password: form.password,
      });
      setForm(prev => ({ ...prev, nome_admin: res.data.nome }));
      setAccountStatus({ state: "ready", exists: true, verified: true });
      toast.success("Account verificato");
    } catch (err) {
      setAccountStatus(prev => ({ ...prev, verified: false }));
      toast.error(err.response?.data?.detail || "Password non valida");
    } finally { setVerifyingAccount(false); }
  };

  const validateField = (key, value) => {
    const errs = { ...formErrors };
    if (key === "partita_iva") {
      if (!value.trim()) errs.partita_iva = "La Partita IVA è obbligatoria";
      else if (!validatePIVA(value)) errs.partita_iva = "Partita IVA non valida (11 cifre, es. 01234567890)";
      else delete errs.partita_iva;
    }
    if (key === "email_azienda") {
      if (!value.trim()) delete errs.email_azienda;
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) errs.email_azienda = "Email non valida";
      else if (/[A-Z]/.test(value)) errs.email_azienda = "L'email deve essere in minuscolo";
      else delete errs.email_azienda;
    }
    if (key === "email_admin") {
      if (!value.trim()) errs.email_admin = "L'email è obbligatoria";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) errs.email_admin = "Email non valida";
      else delete errs.email_admin;
    }
    if (key === "telefono") {
      if (!value.trim()) errs.telefono = "Il telefono è obbligatorio";
      else if (!/^\d{7,15}$/.test(value.trim())) errs.telefono = "Solo numeri (7-15 cifre)";
      else delete errs.telefono;
    }
    setFormErrors(errs);
  };

  const updateFieldWithValidation = (key, value) => {
    updateField(key, value);
    if (formErrors[key]) validateField(key, value);
  };

  const canGoStep1 = selectedPkgs.length > 0;
  const passwordsMatch = form.password.length > 0 && form.password === form.conferma_password;
  const fatturazioneOk = (form.codice_univoco && form.codice_univoco.trim()) ||
    (form.pec && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.pec.trim()));
  const canSubmit = form.nome_azienda && form.partita_iva && validatePIVA(form.partita_iva) &&
    form.sede_legale && form.sede_legale.trim() && fatturazioneOk &&
    form.telefono && /^\d{7,15}$/.test(form.telefono) &&
    form.nome_admin && form.email_admin && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email_admin) && !/[A-Z]/.test(form.email_admin) &&
    accountStatus.state === "ready" && (accountStatus.exists ? accountStatus.verified && form.password :
      form.password && form.password.length >= 6 && passwordsMatch) && form.nome_locale;

  const handleSubmit = async (provider = "stripe") => {
    if (!canSubmit) { toast.error("Compila tutti i campi obbligatori"); return; }
    if (contratto && !contrattoAccettato) { toast.error("Devi accettare i termini del contratto"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/register/checkout`, {
        ...form,
        email_azienda: form.email_azienda || form.email_admin,
        piano: "annuale", selected_packages: selectedPkgs,
        addon_telegram_qty: addonTelegramQty,
        addon_locale_extra: addonLocaleExtra,
        codice_sconto: activePromo?.codice || "",
        origin_url: window.location.origin,
        provider,
        referral_code: referralCode || "",
      });
      if (res.data.status === "trial_activated") {
        // Non dovrebbe più accadere — il backend ora richiede sempre carta
        // Lasciato come safety net per backward compat
        localStorage.removeItem("yollo_referral_code");
        toast.success(res.data.message || "Registrazione completata!");
        navigate("/login");
        return;
      }
      if (res.data.url) {
        sessionStorage.setItem("register_existing_account", accountStatus.exists ? "1" : "0");
        localStorage.removeItem("yollo_referral_code");
        window.location.href = res.data.url;
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Errore durante la registrazione"); }
    finally { setLoading(false); }
  };

  // ─── STEP 3: Success ───
  if (step === 4) {
    const returningAccount = sessionStorage.getItem("register_existing_account") === "1";
    const isPaid = paymentStatus?.payment_status === "paid";
    const isTrialSetup = isPaid && (paymentStatus?.tipo === "trial_setup" || paymentStatus?.tipo === "trial_subscription");
    const isExpired = paymentStatus?.status === "expired";
    const isFailed = paymentStatus?.payment_status === "failed";
    const isStuck = pollStopped && !isPaid;
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(145deg, #F0EFFE 0%, #F5F5FA 50%, #FFF0F4 100%)" }}>
        <div className="w-full max-w-md text-center">
          <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${isPaid ? "bg-emerald-100" : (isExpired || isFailed || isStuck) ? "bg-red-100" : "bg-indigo-100"}`}>
            {isPaid ? <Check className="h-10 w-10 text-emerald-600" /> : (isExpired || isFailed || isStuck) ? <CreditCard className="h-10 w-10 text-red-500" /> : <Loader2 className="h-10 w-10 text-indigo-500 animate-spin" />}
          </div>
          <h1 className="text-2xl font-black text-gray-900 mb-2">
            {isTrialSetup ? "Prova gratuita attivata!" : isPaid ? "Registrazione completata!" : (isExpired || isFailed) ? "Pagamento non riuscito" : isStuck ? "Verifica in sospeso" : "Verifica in corso..."}
          </h1>
          <p className="text-gray-500 mb-4">
            {isTrialSetup
              ? "La tua carta è stata verificata. Nessun addebito è stato effettuato."
              : isPaid ? (returningAccount ? "La nuova società è stata collegata. Accedi con la tua email e password abituali." : "Account creato. Conferma la tua email per accedere.")
              : isExpired ? "La sessione è scaduta. Riprova."
              : isFailed ? "Il pagamento non è andato a buon fine. Riprova."
              : isStuck ? "Verifica manuale in corso. Controlla la tua email o riprova."
              : "Stiamo verificando il tuo pagamento..."}
          </p>
          {isPaid && !returningAccount && (
            <div className="mb-6 p-4 bg-indigo-50 rounded-2xl border border-indigo-200 text-left">
              <div className="flex items-start gap-2">
                <Mail className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-indigo-700 mb-1">Controlla la tua email</p>
                  <p className="text-xs text-indigo-600">
                    Ti abbiamo inviato un link di conferma. Clicca sul link nell'email per attivare definitivamente il tuo account e poter accedere.
                  </p>
                </div>
              </div>
            </div>
          )}
          {isTrialSetup && (
            <div className="mb-6 p-4 bg-emerald-50 rounded-2xl border border-emerald-200 text-left">
              <p className="text-sm font-bold text-emerald-700 mb-1">Cosa succede adesso?</p>
              <ul className="text-xs text-emerald-600 space-y-1">
                <li>• Accedi e usa Yollo gratuitamente durante la prova</li>
                <li>• Alla scadenza del periodo di prova, la carta verrà addebitata automaticamente</li>
                <li>• Potrai disdire in qualsiasi momento dalla sezione "Il mio abbonamento"</li>
              </ul>
            </div>
          )}
          {isPaid && <Button onClick={() => { sessionStorage.removeItem("register_existing_account"); navigate("/login"); }} className="h-12 px-8 text-base font-bold rounded-2xl text-white" style={{ background: "linear-gradient(135deg, #6C63FF, #8B85FF)" }} data-testid="goto-login-btn">Accedi <ArrowRight className="h-4 w-4 ml-2" /></Button>}
          {(isExpired || isFailed || isStuck) && <Button onClick={() => { navigate("/register"); window.location.reload(); }} className="h-12 px-8 text-base font-bold rounded-2xl text-white" style={{ background: "linear-gradient(135deg, #6C63FF, #8B85FF)" }} data-testid="retry-btn">Riprova</Button>}
        </div>
      </div>
    );
  }

  const basePackages = packages;

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(145deg, #F0EFFE 0%, #F5F5FA 50%, #FFF0F4 100%)" }}>
      <div className="fixed top-0 left-0 w-96 h-96 rounded-full opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle, #6C63FF40 0%, transparent 70%)", transform: "translate(-30%, -30%)" }} />
      <div className="fixed bottom-0 right-0 w-96 h-96 rounded-full opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle, #FF658440 0%, transparent 70%)" }} />

      <div className="relative z-10 max-w-5xl mx-auto px-4 py-8">
        <button onClick={() => step === 0 ? navigate("/") : setStep(s => Math.max(0, s - 1))}
          className="flex items-center gap-1.5 text-sm font-semibold text-gray-400 hover:text-gray-600 transition-colors mb-4"
          data-testid="register-back">
          <ArrowLeft className="h-4 w-4" /> {step === 0 ? "Torna alla home" : "Indietro"}
        </button>

        <div className="text-center mb-6">
          <button onClick={() => navigate("/")} className="inline-block cursor-pointer" data-testid="register-logo-home">
            <img src="/yollo-logo.png" alt="Yollo" className="mx-auto mb-3 hover:scale-105 transition-transform" style={{ width: "100px", height: "auto" }} />
          </button>
          <h1 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
            {step === 0 ? "Scegli i pacchetti" : step === 1 ? "Servizi aggiuntivi" : step === 2 ? "I tuoi dati" : "Riepilogo ordine"}
          </h1>
          <p className="text-gray-500 mt-1.5">
            {step === 0 ? "Seleziona i moduli per il tuo locale" : step === 1 ? "Aggiungi Bot Telegram e locali extra" : step === 2 ? "Inserisci i dati del tuo account" : "Controlla e conferma"}
          </p>
          {hasTrial && step === 0 && (
            <div className="mt-3 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-sm font-bold">
              <Sparkles className="h-4 w-4" /> {trialInfo.giorni} giorni di prova gratuita
            </div>
          )}
        </div>

        {/* Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {["Pacchetti", "Add-on", "Dati", "Conferma"].map((label, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i < step ? "bg-indigo-500 text-white" : i === step ? "bg-indigo-500 text-white shadow-lg shadow-indigo-200" : "bg-gray-200 text-gray-400"}`}>
                {i < step ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`text-xs font-semibold hidden sm:inline ${i <= step ? "text-gray-700" : "text-gray-400"}`}>{label}</span>
              {i < 3 && <ChevronRight className="h-4 w-4 text-gray-300" />}
            </div>
          ))}
        </div>

        {/* ─── STEP 0: Pacchetti ─── */}
        {step === 0 && (
          <div>
            {/* Combo Offer */}
            {comboRules.length > 0 && (
              <div className="mb-6">
                <div className="relative overflow-hidden rounded-[20px] border-2 border-indigo-200 p-6" style={{ background: "linear-gradient(135deg, #F5F3FF, #EEF2FF, #FDF2F8)" }}>
                  <div className="absolute top-0 right-0 px-4 py-1.5 rounded-bl-2xl text-[11px] font-black text-white" style={{ background: "linear-gradient(135deg, #EF4444, #F97316)" }}>
                    RISPARMIA COMBINANDO
                  </div>
                  <div className="flex items-center gap-2 mb-3 mt-2">
                    <Sparkles className="h-5 w-5" style={{ color: "#6C63FF" }} />
                    <h3 className="text-lg font-black text-gray-900">Offerte Combo</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {comboRules.map((rule, idx) => (
                      <div key={idx} className="bg-white/80 rounded-xl p-3 text-center border border-gray-100">
                        <p className="text-xs font-bold text-indigo-600">{rule.label}</p>
                        <p className="text-2xl font-black mt-1" style={{ color: "#6C63FF" }}>{rule.prezzo_mensile}<span className="text-sm text-gray-400 font-normal">/mese + IVA</span></p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
              {basePackages.map(pkg => {
                const isSelected = selectedPkgs.includes(pkg.id);
                const colors = TIPO_COLORS[pkg.tipo] || TIPO_COLORS.base;
                const IconComp = TIPO_ICONS[pkg.tipo] || Thermometer;
                const hasAcq = pkg.nome.toLowerCase().includes(acquiKeyword);
                return (
                  <button type="button" key={pkg.id} onClick={() => togglePkg(pkg.id)}
                    className={`relative text-left rounded-[24px] border-2 overflow-hidden transition-all cursor-pointer flex flex-col h-full ${
                      isSelected ? `${colors.bg} ${colors.border} shadow-xl` : "bg-white border-gray-200 hover:border-gray-300 hover:shadow-lg"
                    }`} data-testid={`pkg-${pkg.id}`}>
                    
                    {/* Immagine pacchetto 16:9 */}
                    {pkg.immagine_url ? (
                      <div className="relative w-full aspect-[16/9] overflow-hidden">
                        <img 
                          src={pkg.immagine_url.startsWith("http") ? pkg.immagine_url : `${API}/superadmin/files/${pkg.immagine_url}`}
                          alt={pkg.nome}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                        {pkg.in_evidenza && (
                          <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-[10px] font-bold bg-yellow-400 text-yellow-900 flex items-center gap-1 shadow-md">
                            <Star className="h-3 w-3 fill-yellow-900" /> CONSIGLIATO
                          </div>
                        )}
                        {/* Checkbox selezione su immagine */}
                        <div className={`absolute top-3 right-3 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all ${
                          isSelected ? 'border-white bg-white' : 'border-white/60 bg-white/30'
                        }`}>
                          {isSelected && <Check className="h-4 w-4" style={{ color: colors.accent }} />}
                        </div>
                      </div>
                    ) : (
                      <div className="p-5 pb-0 relative">
                        {pkg.in_evidenza && (
                          <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-[10px] font-bold bg-yellow-400 text-yellow-900 flex items-center gap-1 shadow-sm">
                            <Star className="h-3 w-3 fill-yellow-900" /> CONSIGLIATO
                          </div>
                        )}
                        {/* Checkbox selezione */}
                        <div className={`absolute top-3 right-3 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all ${
                          isSelected ? `border-current` : 'border-gray-300'
                        }`} style={isSelected ? { borderColor: colors.accent, backgroundColor: colors.accent } : {}}>
                          {isSelected && <Check className="h-4 w-4 text-white" />}
                        </div>
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center mt-6 mb-2" style={{ backgroundColor: `${colors.accent}15` }}>
                          <IconComp className="h-6 w-6" style={{ color: colors.accent }} />
                        </div>
                      </div>
                    )}
                    
                    {/* Contenuto card */}
                    <div className={`flex flex-col flex-grow ${pkg.immagine_url ? 'p-5 pt-4' : 'p-5 pt-2'}`}>
                      <h3 className="font-bold text-gray-900 text-lg mb-1">{pkg.nome}</h3>
                      {hasAcq && <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold mb-2 w-fit">PREMIUM</span>}
                      <p className="text-gray-500 text-xs mb-4 leading-relaxed min-h-[36px]">{pkg.descrizione}</p>
                      
                      {/* Prezzo */}
                      <div className="mb-4">
                        <span className="text-3xl font-black text-gray-900">{pkg.prezzo_mensile_annuale}</span>
                        <span className="text-gray-400 text-sm">€/mese + IVA</span>
                      </div>
                      
                      {/* Features - flex-grow */}
                      <div className="space-y-2 flex-grow">
                        {(pkg.feature_labels || pkg.features || []).slice(0, 5).map((f, fi) => (
                          <div key={fi} className="flex items-center gap-2 text-xs">
                            <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${colors.accent}15` }}>
                              <Check className="h-2.5 w-2.5" style={{ color: colors.accent }} />
                            </div>
                            <span className="text-gray-600">{typeof f === "string" ? (FEATURE_LABELS[f] || f) : f}</span>
                          </div>
                        ))}
                        {(pkg.feature_labels || pkg.features || []).length > 5 && (
                          <div className="text-[10px] text-gray-400 pl-6">+{(pkg.feature_labels || pkg.features).length - 5} altre</div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Total + CTA */}
            <div className="bg-white rounded-[20px] p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Totale pacchetti</p>
                  {cart ? (
                    <div>
                      <div className="flex items-baseline gap-2">
                        {cart.combo_applied && cart.risparmio > 0 ? (
                          <>
                            <span className="text-3xl font-black text-gray-900">{cart.subtotal_pacchetti.toFixed(2)}<span className="text-base font-normal text-gray-400">/mese + IVA</span></span>
                            <span className="text-base line-through text-gray-300 font-semibold">{cart.somma_singoli.toFixed(2)}€</span>
                          </>
                        ) : (
                          <span className="text-3xl font-black text-gray-900">{cart.subtotal_pacchetti.toFixed(2)}<span className="text-base font-normal text-gray-400">/mese + IVA</span></span>
                        )}
                      </div>
                      {cart.combo_applied && cart.risparmio > 0 && (
                        <p className="text-xs font-bold mt-0.5" style={{ color: "#6C63FF" }}>
                          {cart.combo_applied} — Risparmi {cart.risparmio.toFixed(2)}€/mese!
                        </p>
                      )}
                      {hasTrial && <p className="text-xs text-emerald-600 font-semibold mt-0.5">Primi {trialInfo.giorni} giorni gratis</p>}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">Seleziona almeno un pacchetto</p>
                  )}
                </div>
                <Button onClick={() => setStep(1)} disabled={!canGoStep1}
                  className="h-12 px-8 text-base font-bold rounded-2xl text-white"
                  style={{ background: canGoStep1 ? "linear-gradient(135deg, #6C63FF, #8B85FF)" : "#D1D5DB" }}
                  data-testid="step0-next-btn">
                  Continua <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ─── STEP 1: Add-on ─── */}
        {step === 1 && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
              {/* Bot Telegram */}
              <div className={`rounded-[20px] border-2 p-6 transition-all ${
                addonTelegramQty > 0 ? "bg-blue-50/60 border-blue-300 shadow-lg" : "bg-white border-gray-200"
              }`} data-testid="reg-addon-telegram">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                    <Bot className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Bot Telegram AI</h3>
                    <p className="text-sm text-gray-500">Assistente intelligente per il tuo locale</p>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Primo numero</span><span className="font-bold text-gray-900">{(addonsConfig.telegram_bot?.prezzo_mensile || 39.90).toFixed(2)}€/mese + IVA</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Ogni numero extra</span><span className="font-bold text-gray-900">{(addonsConfig.telegram_bot?.prezzo_numero_extra || 5.00).toFixed(2)}€/mese + IVA</span></div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Quanti numeri vuoi associare?</span>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setAddonTelegramQty(Math.max(0, addonTelegramQty - 1))}
                      className="w-9 h-9 rounded-xl border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-100 font-bold text-lg" data-testid="tg-minus">-</button>
                    <span className="w-8 text-center text-xl font-black text-gray-900" data-testid="tg-qty">{addonTelegramQty}</span>
                    <button type="button" onClick={() => setAddonTelegramQty(addonTelegramQty + 1)}
                      className="w-9 h-9 rounded-xl border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-100 font-bold text-lg" data-testid="tg-plus">+</button>
                  </div>
                </div>
              </div>

              {/* Locale Extra */}
              <div className={`rounded-[20px] border-2 p-6 transition-all ${
                addonLocaleExtra > 0 ? "bg-amber-50/60 border-amber-300 shadow-lg" : "bg-white border-gray-200"
              }`} data-testid="reg-addon-locale">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center">
                    <Store className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Locale Extra</h3>
                    <p className="text-sm text-gray-500">Gestisci piu sedi dalla stessa piattaforma</p>
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Per ogni locale aggiuntivo</span><span className="font-bold text-gray-900">{addonsConfig.locale_extra?.prezzo_mensile || 89}/mese + IVA</span></div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Quanti locali extra?</span>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setAddonLocaleExtra(Math.max(0, addonLocaleExtra - 1))}
                      className="w-9 h-9 rounded-xl border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-100 font-bold text-lg" data-testid="loc-minus">-</button>
                    <span className="w-8 text-center text-xl font-black text-gray-900" data-testid="loc-qty">{addonLocaleExtra}</span>
                    <button type="button" onClick={() => setAddonLocaleExtra(addonLocaleExtra + 1)}
                      className="w-9 h-9 rounded-xl border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-100 font-bold text-lg" data-testid="loc-plus">+</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Promo Code */}
            <div className="bg-white rounded-[20px] p-4 mb-4 border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-500">Hai un codice sconto?</span>
              </div>
              {!activePromo ? (
                <div className="flex gap-2 mt-2">
                  <Input value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())}
                    className="h-10 rounded-xl bg-gray-50 border-gray-200 text-gray-900 font-mono tracking-wider" placeholder="CODICE2026"
                    onKeyDown={e => e.key === "Enter" && validatePromo()} data-testid="promo-code-input" />
                  <Button onClick={validatePromo} disabled={promoLoading || !promoCode.trim()}
                    variant="outline" className="rounded-xl border-gray-200 text-gray-600 px-4" data-testid="apply-promo-btn">
                    {promoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Applica"}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between mt-2 p-2.5 rounded-xl bg-emerald-50 border border-emerald-200">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-emerald-600" />
                    <span className="font-mono font-bold text-emerald-700">{activePromo.codice}</span>
                    <span className="text-xs text-emerald-600">
                      {activePromo.tipo_sconto === "percentuale" ? `-${activePromo.valore}%` : `-${activePromo.valore}€`}
                    </span>
                  </div>
                  <button onClick={removePromo} className="text-xs text-gray-400 hover:text-red-500" data-testid="remove-promo-btn">Rimuovi</button>
                </div>
              )}
            </div>

            {/* Total + CTA */}
            <div className="bg-white rounded-[20px] p-5 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Totale mensile</p>
                  {cart ? (
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-black text-gray-900">{cart.subtotal.toFixed(2)}<span className="text-base font-normal text-gray-400">/mese + IVA</span></span>
                        {cart.sconto ? (
                          <span className="text-base line-through text-gray-300 font-semibold">{cart.subtotal_pre_sconto.toFixed(2)}€</span>
                        ) : (cart.combo_applied && cart.risparmio > 0 && (
                          <span className="text-base line-through text-gray-300 font-semibold">{(cart.somma_singoli + cart.subtotal_addons).toFixed(2)}€</span>
                        ))}
                      </div>
                      {cart.sconto && (
                        <p className="text-xs font-bold mt-0.5 text-emerald-600" data-testid="cart-sconto-line">
                          Codice {cart.sconto.codice} — {cart.sconto.tipo_sconto === "percentuale" ? `-${cart.sconto.valore}%` : `-${cart.sconto.valore}€`} ({cart.sconto.durata_sconto === "primo_mese" ? "primo mese" : "ricorrente"})
                        </p>
                      )}
                      {!cart.sconto && cart.combo_applied && cart.risparmio > 0 && (
                        <p className="text-xs font-bold mt-0.5" style={{ color: "#6C63FF" }}>
                          {cart.combo_applied} — Risparmi {cart.risparmio.toFixed(2)}€/mese!
                        </p>
                      )}
                      {cart.sconto_primo_mese && (
                        <p className="text-xs font-bold mt-0.5 text-emerald-600" data-testid="cart-sconto-primo-mese">
                          Codice {cart.sconto_primo_mese.codice}: 1° mese {(cart.subtotal - cart.sconto_primo_mese.importo).toFixed(2)}€ ({cart.sconto_primo_mese.tipo_sconto === "percentuale" ? `-${cart.sconto_primo_mese.valore}%` : `-${cart.sconto_primo_mese.valore}€`}), poi {cart.subtotal.toFixed(2)}€/mese
                        </p>
                      )}
                    </div>
                  ) : (
                    <span className="text-2xl font-black text-gray-900">{selectedPkgs.length > 0 ? "Calcolo..." : "0.00"}<span className="text-base font-normal text-gray-400">/mese + IVA</span></span>
                  )}
                </div>
                <Button onClick={() => setStep(2)}
                  className="h-12 px-8 text-base font-bold rounded-2xl text-white"
                  style={{ background: "linear-gradient(135deg, #6C63FF, #8B85FF)" }}
                  data-testid="step1-next-btn">
                  Continua <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ─── STEP 2: Dati ─── */}
        {step === 2 && (
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-[20px] p-6 shadow-sm border border-gray-100 space-y-5">
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> Dati Azienda</h3>
                <div className="space-y-3">
                  <div><Label className="text-xs font-semibold text-gray-600">Nome Azienda *</Label><Input value={form.nome_azienda} onChange={e => updateField("nome_azienda", e.target.value)} className="h-12 rounded-xl border-gray-200 bg-gray-50 text-gray-900" placeholder="Es. Ristorante Da Mario S.r.l." required data-testid="reg-nome-azienda" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold text-gray-600">Partita IVA *</Label>
                      <Input
                        value={form.partita_iva}
                        onChange={e => updateFieldWithValidation("partita_iva", e.target.value)}
                        onBlur={e => validateField("partita_iva", e.target.value)}
                        className={`h-12 rounded-xl bg-gray-50 text-gray-900 ${formErrors.partita_iva ? "border-red-400" : "border-gray-200"}`}
                        placeholder="01234567890"
                        data-testid="reg-piva"
                      />
                      {formErrors.partita_iva && <p className="text-xs text-red-500 mt-1">{formErrors.partita_iva}</p>}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-600">Sede legale *</Label>
                    <Input
                      value={form.sede_legale}
                      onChange={e => updateField("sede_legale", e.target.value)}
                      className="h-12 rounded-xl border-gray-200 bg-gray-50 text-gray-900"
                      placeholder="Es. Via Roma 10, 00100 Roma (RM)"
                      required
                      data-testid="reg-sede-legale"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-600">Codice Fiscale</Label>
                    <Input
                      value={form.codice_fiscale}
                      onChange={e => updateField("codice_fiscale", e.target.value)}
                      className="h-12 rounded-xl border-gray-200 bg-gray-50 text-gray-900"
                      placeholder="Facoltativo (di norma uguale alla P.IVA)"
                      data-testid="reg-codice-fiscale"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold text-gray-600">Codice Univoco (SDI)</Label>
                      <Input
                        value={form.codice_univoco}
                        onChange={e => updateField("codice_univoco", e.target.value.toUpperCase())}
                        className="h-12 rounded-xl border-gray-200 bg-gray-50 text-gray-900"
                        placeholder="Es. M5UXCR1"
                        data-testid="reg-codice-univoco"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-600">PEC</Label>
                      <Input
                        type="email"
                        value={form.pec}
                        onChange={e => updateField("pec", e.target.value.toLowerCase().trim())}
                        className="h-12 rounded-xl border-gray-200 bg-gray-50 text-gray-900 lowercase"
                        placeholder="azienda@pec.it"
                        data-testid="reg-pec"
                      />
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-500 -mt-1" data-testid="reg-fatturazione-hint">
                    Per la fatturazione elettronica indica il <strong>Codice Univoco</strong> oppure, se non ce l'hai, la <strong>PEC</strong> (almeno uno è obbligatorio).
                  </p>
                  <div>
                    <Label className="text-xs font-semibold text-gray-600">Email aziendale per le fatture</Label>
                    <Input type="email" value={form.email_azienda} data-testid="reg-email-azienda"
                      onChange={e => updateFieldWithValidation("email_azienda", e.target.value.toLowerCase().trim())}
                      autoCapitalize="none" autoCorrect="off" spellCheck={false}
                      className="h-12 rounded-xl border-gray-200 bg-gray-50 text-gray-900" placeholder="Se vuota, usa l’email dell’account" />
                    {formErrors.email_azienda && <p className="text-xs text-red-500 mt-1" data-testid="reg-email-azienda-error">{formErrors.email_azienda}</p>}
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> Account Amministratore</h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs font-semibold text-gray-600">Nome e Cognome *</Label><Input value={form.nome_admin} onChange={e => updateField("nome_admin", e.target.value)} readOnly={accountStatus.exists && accountStatus.verified} className="h-12 rounded-xl border-gray-200 bg-gray-50 text-gray-900" placeholder="Mario Rossi" required data-testid="reg-nome-admin" /></div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-600">Telefono *</Label>
                      <Input
                        value={form.telefono}
                        onChange={e => updateFieldWithValidation("telefono", e.target.value.replace(/\D/g, ''))}
                        onBlur={e => validateField("telefono", e.target.value)}
                        className={`h-12 rounded-xl bg-gray-50 text-gray-900 ${formErrors.telefono ? "border-red-400" : "border-gray-200"}`}
                        placeholder="3331234567"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={15}
                        data-testid="reg-telefono"
                      />
                      {formErrors.telefono && <p className="text-xs text-red-500 mt-1">{formErrors.telefono}</p>}
                    </div>
                  </div>
                  <div><Label className="text-xs font-semibold text-gray-600">Email *</Label><Input type="email" value={form.email_admin} onChange={e => updateFieldWithValidation("email_admin", e.target.value.toLowerCase().trim())} onBlur={e => validateField("email_admin", e.target.value)} autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email" className={`h-12 rounded-xl bg-gray-50 text-gray-900 lowercase ${formErrors.email_admin ? "border-red-400" : "border-gray-200"}`} placeholder="mario@email.com" required data-testid="reg-email-admin" />{formErrors.email_admin && <p className="text-xs text-red-500 mt-1">{formErrors.email_admin}</p>}</div>
                  {accountStatus.state === "checking" && <p data-testid="reg-account-checking" className="text-xs text-gray-500">Verifica account…</p>}
                  {accountStatus.state === "error" && <p role="alert" data-testid="reg-account-error" className="text-xs text-red-600">{accountStatus.message}</p>}
                  {accountStatus.exists && <p data-testid="reg-account-existing" className="text-sm text-teal-700">Account già presente: conferma la tua password per collegare la nuova società.</p>}
                  <div><Label className="text-xs font-semibold text-gray-600">{accountStatus.exists ? "Password dell’account esistente *" : "Password *"}</Label><Input type="password" value={form.password} onChange={e => updateField("password", e.target.value)} className="h-12 rounded-xl border-gray-200 bg-gray-50 text-gray-900" placeholder={accountStatus.exists ? "La tua password attuale" : "Min. 6 caratteri"} required minLength={accountStatus.exists ? 1 : 6} data-testid="reg-password" /></div>
                  {accountStatus.exists && <button type="button" data-testid="reg-verify-account" disabled={verifyingAccount || !form.password || accountStatus.verified} onClick={verifyExistingAccount} className="inline-flex items-center gap-2 px-4 py-2 bg-teal-700 text-white text-sm font-semibold disabled:opacity-60 hover:bg-teal-800 transition-colors">{verifyingAccount ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}{accountStatus.verified ? "Account verificato" : "Verifica password"}</button>}
                  {!accountStatus.exists && <div>
                    <Label className="text-xs font-semibold text-gray-600">Conferma password *</Label>
                    <Input
                      type="password"
                      value={form.conferma_password}
                      onChange={e => updateField("conferma_password", e.target.value)}
                      className={`h-12 rounded-xl bg-gray-50 text-gray-900 ${
                        form.conferma_password.length > 0 && form.password !== form.conferma_password
                          ? "border-red-400"
                          : passwordsMatch
                          ? "border-emerald-400"
                          : "border-gray-200"
                      }`}
                      placeholder="Ripeti la password"
                      required
                      minLength={6}
                      data-testid="reg-conferma-password"
                    />
                    {form.conferma_password.length > 0 && form.password !== form.conferma_password && (
                      <p className="text-xs text-red-500 mt-1">Le password non coincidono</p>
                    )}
                    {passwordsMatch && (
                      <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                        <Check className="h-3 w-3" /> Le password coincidono
                      </p>
                    )}
                  </div>}
                </div>
              </div>
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Primo Locale</h3>
                <div><Label className="text-xs font-semibold text-gray-600">Nome del Locale *</Label><Input value={form.nome_locale} onChange={e => updateField("nome_locale", e.target.value)} className="h-12 rounded-xl border-gray-200 bg-gray-50 text-gray-900" placeholder="Es. Ristorante Da Mario - Centro" required data-testid="reg-nome-locale" /></div>
              </div>
            </div>
            <div className="flex justify-between mt-6">
              <Button variant="ghost" onClick={() => setStep(1)} className="h-12 px-6 rounded-2xl text-gray-500 font-semibold" data-testid="step2-back-btn"><ArrowLeft className="h-4 w-4 mr-2" /> Indietro</Button>
              <Button onClick={() => {
                // Valida tutti i campi obbligatori e mostra errori
                const errs = {};
                if (!form.partita_iva?.trim()) errs.partita_iva = "La Partita IVA è obbligatoria";
                else if (!validatePIVA(form.partita_iva)) errs.partita_iva = "Partita IVA non valida (11 cifre, es. 01234567890)";
                if (!form.telefono?.trim()) errs.telefono = "Il telefono è obbligatorio";
                else if (!/^\d{7,15}$/.test(form.telefono.trim())) errs.telefono = "Solo numeri (7-15 cifre)";
                if (!form.email_admin?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email_admin)) errs.email_admin = "Email non valida";
                else if (/[A-Z]/.test(form.email_admin)) errs.email_admin = "L'email deve essere in minuscolo";
                if (accountStatus.exists ? !accountStatus.verified : (!form.password || form.password.length < 6)) errs.password = accountStatus.exists ? "Verifica la password dell'account" : "La password deve avere almeno 6 caratteri";
                else if (!accountStatus.exists && form.password !== form.conferma_password) errs.conferma_password = "Le password non coincidono";
                if (form.email_azienda && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email_azienda)) errs.email_azienda = "Email aziendale non valida";
                setFormErrors(errs);
                if (canSubmit && Object.keys(errs).length === 0) setStep(3);
                else toast.error("Compila tutti i campi obbligatori correttamente");
              }} disabled={!canSubmit}
                className="h-12 px-8 text-base font-bold rounded-2xl text-white" style={{ background: canSubmit ? "linear-gradient(135deg, #6C63FF, #8B85FF)" : "#D1D5DB" }} data-testid="step2-next-btn">
                Continua <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ─── STEP 3: Riepilogo ─── */}
        {step === 3 && (
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-[20px] p-6 shadow-sm border border-gray-100 space-y-5">
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Pacchetti e servizi selezionati</h3>
                {cart && (
                  <div className="space-y-2">
                    {/* Combo or individual packages */}
                    {cart.combo_applied ? (
                      <div className="p-3 rounded-xl mb-2 border border-indigo-200" style={{ background: "#F5F3FF" }}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-indigo-500" />
                            <span className="font-bold text-indigo-700 text-sm">{cart.combo_applied}</span>
                          </div>
                          <span className="font-black text-gray-900">{cart.subtotal_pacchetti.toFixed(2)}/mese</span>
                        </div>
                        <p className="text-[11px] text-indigo-400 mt-1">{cart.pacchetti.map(p => p.nome).join(" + ")}</p>
                      </div>
                    ) : (
                      cart.pacchetti.map(p => (
                        <div key={p.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50">
                          <div className="flex items-center gap-2">
                            <Check className="h-4 w-4 text-indigo-500" />
                            <span className="font-semibold text-gray-900 text-sm">{p.nome}</span>
                          </div>
                          <span className="font-bold text-gray-900">{p.prezzo_singolo}/mese</span>
                        </div>
                      ))
                    )}
                    {/* Addon items */}
                    {cart.addon_items.map((a, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-gray-50">
                        <div className="flex items-center gap-2">
                          <Check className="h-4 w-4 text-blue-500" />
                          <span className="font-semibold text-gray-900 text-sm">{a.nome}</span>
                        </div>
                        <span className="font-bold text-gray-900">{a.prezzo.toFixed(2)}/mese</span>
                      </div>
                    ))}
                    {activePromo && (
                      <div className="flex items-center justify-between p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                        <div className="flex items-center gap-2"><Tag className="h-4 w-4 text-emerald-600" /><span className="font-mono text-sm font-bold text-emerald-700">{activePromo.codice}</span></div>
                        <span className="text-sm text-emerald-600 font-semibold">{activePromo.durata_sconto === "primo_mese" ? "Sconto primo mese" : "Sconto ricorrente"}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Dati registrazione</h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {[["Azienda", form.nome_azienda], ["Admin", form.nome_admin], ["Email", form.email_admin], ["Email fatture", form.email_azienda || form.email_admin], ["Locale", form.nome_locale], ["Piano", "Annuale (mensile)"], ...(accountStatus.exists ? [["Account", "Già esistente · verificato"]] : [])].map(([l, v]) => (
                    <div key={l} className="contents"><div><span className="text-gray-400">{l}:</span></div><div className="font-semibold text-gray-900 capitalize">{v}</div></div>
                  ))}
                </div>
              </div>
              {cart && (
                <div className="border-t border-gray-100 pt-4">
                  {cart.combo_applied && cart.risparmio > 0 && (
                    <div className="flex items-center gap-2 mb-3 p-2.5 rounded-xl" style={{ background: "#EEF2FF" }}>
                      <Sparkles className="h-4 w-4 text-indigo-500" />
                      <span className="text-xs font-bold text-indigo-700">
                        {cart.combo_applied} applicata — Risparmi {cart.risparmio.toFixed(2)}€/mese rispetto ai singoli ({cart.somma_singoli.toFixed(2)}€)
                      </span>
                    </div>
                  )}
                  {hasTrial ? (
                    <>
                      {/* Phase A: trial flow — €0 oggi, addebito alla scadenza */}
                      <div className="flex items-center justify-between pt-2">
                        <span className="text-gray-700 font-semibold">Da pagare oggi</span>
                        <div className="text-right">
                          <div className="flex items-baseline gap-2 justify-end">
                            <span className="text-3xl font-black" style={{ color: "#10B981" }} data-testid="trial-due-today">0,00€</span>
                          </div>
                          <p className="text-[11px] text-emerald-600 font-semibold">Nessun addebito ora</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-3">
                        <div>
                          <span className="text-gray-500 text-sm">Dopo i {trialInfo.giorni} giorni di prova</span>
                          <p className="text-[11px] text-gray-400 mt-0.5">Addebito automatico mensile</p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-baseline gap-2 justify-end">
                            <span className="text-xl font-bold text-gray-900" data-testid="trial-recurring-price">{cart.totale_mensile.toFixed(2)}€</span>
                            <span className="text-xs text-gray-400">/mese</span>
                          </div>
                          <p className="text-[10px] text-gray-400">IVA inclusa ({ivaPct}%)</p>
                        </div>
                      </div>
                      <div className="mt-4 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                        <p className="text-sm text-emerald-700 font-semibold flex items-center gap-1.5">
                          <Sparkles className="h-4 w-4" /> {trialInfo.giorni} giorni di prova gratuita — accesso completo a tutta la piattaforma
                        </p>
                        <p className="text-xs text-emerald-600 mt-1">
                          Inserisci la carta per attivare. Puoi disdire o cambiare configurazione entro fine prova.
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-between pt-2">
                        <span className="text-gray-700 font-semibold">Totale mensile</span>
                        <div className="text-right">
                          <div className="flex items-baseline gap-2 justify-end">
                            <span className="text-2xl font-black text-gray-900">{cart.subtotal.toFixed(2)}<span className="text-sm font-normal text-gray-400">/mese + IVA</span></span>
                            {cart.sconto ? (
                              <span className="text-sm line-through text-gray-300">{cart.subtotal_pre_sconto.toFixed(2)}€</span>
                            ) : (cart.combo_applied && cart.risparmio > 0 && (
                              <span className="text-sm line-through text-gray-300">{(cart.somma_singoli + cart.subtotal_addons).toFixed(2)}€</span>
                            ))}
                          </div>
                          {cart.sconto && (
                            <p className="text-[11px] font-bold text-emerald-600 mt-0.5">Codice {cart.sconto.codice} applicato ({cart.sconto.tipo_sconto === "percentuale" ? `-${cart.sconto.valore}%` : `-${cart.sconto.valore}€`})</p>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Al pagamento verra aggiunta l'IVA ({ivaPct}%): {cart.iva_importo.toFixed(2)}/mese — Totale lordo: {cart.totale_mensile.toFixed(2)}/mese</p>
                    </>
                  )}
                </div>
              )}

              {/* Referral badge */}
              {referralInfo && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-violet-50 border border-violet-200 mt-4">
                  <Users className="h-4 w-4 text-violet-600" />
                  <span className="text-sm text-violet-700">
                    Invitato da <strong>{referralInfo.venditore_nome}</strong>
                  </span>
                </div>
              )}

              {/* Contratto di servizio */}
              {contratto && (
                <div className="border-t border-gray-100 pt-4 mt-4" data-testid="contratto-section">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <ScrollText className="h-3.5 w-3.5" /> Termini e Condizioni del Servizio
                  </h3>
                  <div
                    className="max-h-48 overflow-y-auto border border-gray-200 rounded-xl p-4 bg-gray-50 text-xs text-gray-700 leading-relaxed"
                    data-testid="contratto-scroll"
                    ref={(el) => {
                      if (el && !contrattoScrolled && el.scrollHeight <= el.clientHeight + 5) {
                        setContrattoScrolled(true);
                      }
                    }}
                    onScroll={(e) => {
                      const el = e.target;
                      if (el.scrollHeight - el.scrollTop - el.clientHeight < 30) {
                        setContrattoScrolled(true);
                      }
                    }}
                  >
                    {(() => {
                      const vars = {
                        denominazione: form.nome_azienda || "___",
                        piva: form.partita_iva || "___",
                        codice_fiscale: form.codice_fiscale || form.partita_iva || "___",
                        indirizzo: form.sede_legale || "___",
                        email: form.email_admin || "___",
                        telefono: form.telefono || "___",
                        codice_univoco: form.codice_univoco || "—",
                        pec: form.pec || "—",
                        nome_admin: form.nome_admin || "___",
                        pacchetti: (cart?.pacchetti?.map(p => p.nome).join(", ")) || selectedPkgs.join(", ") || "___",
                        importo: cart ? cart.totale_mensile.toFixed(2) : "___",
                        data: new Date().toLocaleDateString("it-IT"),
                      };
                      let raw = contratto.contenuto || "";
                      const hasTags = /<[a-z][\s\S]*>/i.test(raw);
                      if (!hasTags) {
                        raw = raw
                          .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
                          .split("\n").join("<br/>");
                      }
                      const filled = raw.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? vars[k] : m));
                      return (
                        <div
                          className="prose prose-sm max-w-none prose-headings:my-2 prose-p:my-1.5"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(filled) }}
                        />
                      );
                    })()}
                  </div>
                  {!contrattoScrolled && (
                    <p className="text-[11px] text-amber-600 mt-1.5 font-medium">Scorri fino in fondo per poter accettare</p>
                  )}
                  <label className={`flex items-start gap-2.5 mt-3 cursor-pointer ${!contrattoScrolled ? "opacity-50 pointer-events-none" : ""}`} data-testid="contratto-accept-label">
                    <input
                      type="checkbox"
                      checked={contrattoAccettato}
                      onChange={(e) => setContrattoAccettato(e.target.checked)}
                      disabled={!contrattoScrolled}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      data-testid="contratto-accept-checkbox"
                    />
                    <span className="text-xs text-gray-600 leading-relaxed">
                      Dichiaro di aver letto e di accettare integralmente i <strong>Termini e Condizioni</strong> del servizio Yollo.
                    </span>
                  </label>
                </div>
              )}
            </div>
            <div className="flex flex-wrap justify-between gap-3 mt-6">
              <Button variant="ghost" onClick={() => setStep(2)} className="h-12 px-6 rounded-2xl text-gray-500 font-semibold flex-shrink-0" data-testid="step3-back-btn"><ArrowLeft className="h-4 w-4 mr-2" /> Indietro</Button>
              {hasTrial ? (
                <Button onClick={() => handleSubmit("stripe")} disabled={loading || (contratto && !contrattoAccettato)}
                  className="h-auto min-h-[3rem] px-5 py-2 text-sm sm:text-base font-bold rounded-2xl text-white shadow-lg whitespace-normal text-center leading-tight flex-1 min-w-0"
                  style={{ background: (contratto && !contrattoAccettato) ? "#D1D5DB" : "linear-gradient(135deg, #6C63FF, #8B85FF)", boxShadow: (contratto && !contrattoAccettato) ? "none" : "0 4px 16px rgba(108,99,255,0.35)" }}
                  data-testid="reg-submit-btn">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CreditCard className="h-4 w-4 mr-2 flex-shrink-0" />Inserisci carta &amp; inizia prova <ArrowRight className="h-4 w-4 ml-2 flex-shrink-0" /></>}
                </Button>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => handleSubmit("stripe")} disabled={loading || (contratto && !contrattoAccettato)}
                    className="h-12 px-5 text-sm font-bold rounded-2xl text-white"
                    style={{ background: (contratto && !contrattoAccettato) ? "#D1D5DB" : "linear-gradient(135deg, #6C63FF, #8B85FF)" }}
                    data-testid="reg-pay-stripe-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4 mr-1.5" /> Carta</>}
                  </Button>
                  <Button onClick={() => handleSubmit("paypal")} disabled={loading || (contratto && !contrattoAccettato)}
                    variant="outline"
                    className="h-12 px-5 text-sm font-bold rounded-2xl border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid="reg-pay-paypal-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>
                      <svg className="h-4 w-4 mr-1.5 flex-shrink-0" viewBox="0 0 24 24" fill="none"><path d="M7.144 19.532l1.049-5.751c.11-.606.691-1.002 1.29-.862 2.77.649 6.353.268 8.456-2.132 1.721-1.965 2.257-4.768 1.584-7.226C18.626 1.027 15.892 0 12.887 0H6.064a1.34 1.34 0 00-1.322 1.14L2.055 19.27a.806.806 0 00.794.942h3.396a.899.899 0 00.899-.68z" fill="#003087"/><path d="M21.466 7.164c-.92 4.368-4.16 6.556-8.488 6.556H11.08l-1.17 6.438-.331 1.83a.529.529 0 00.523.622h3.032c.47 0 .87-.34.943-.806l.039-.2.744-4.09.048-.254c.073-.466.474-.806.943-.806h.594c3.847 0 6.861-1.708 7.741-6.648.368-2.065.177-3.788-.795-5.002-.294-.367-.66-.674-1.084-.924l-.599 3.284z" fill="#009cde"/></svg>
                      PayPal
                    </>}
                  </Button>
                </div>
              )}
            </div>
            <p className="text-center text-sm text-gray-400 mt-6">
              Hai già un account? <button type="button" onClick={() => navigate("/login")} className="font-semibold hover:underline" style={{ color: "#6C63FF" }}>Accedi</button>
            </p>
            <p className="text-center text-xs text-gray-400 mt-3">
              Procedendo accetti la nostra{" "}
              <a href="/privacy" target="_blank" className="underline hover:text-gray-600" style={{ color: "#6C63FF" }}>Privacy Policy</a>
              {" "}e la{" "}
              <a href="/cookie-policy" target="_blank" className="underline hover:text-gray-600" style={{ color: "#6C63FF" }}>Cookie Policy</a>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;
